// @ts-nocheck - Amplify Gen 2 type compatibility issues
/**
 * G12w Tariff Calculator (Weekend Tariff)
 *
 * @description Calculates costs for G12w weekend tariff
 * @version 2.1.0
 * @created 2025-01-26
 * @author eCompanion2 Energy Tariff Calculator
 *
 * Extracted from tariff-calculator.ts for better modularity
 */

import type { G12wPricing, MonthlyResult, ConsumptionPattern } from './types';
import {
  VAT_RATE,
  HANDLING_FEE,
  SUBSCRIPTION_FEE,
  TRANSITION_FEE,
  POWER_FEE,
  OZE_FEE,
  COGENERATION_FEE,
  isPowerFeeFrozen,
  isG12DayHour,
  isG12DayHourForOSD,
} from './constants';
import { roundToTwoDecimals, distributeConsumption } from './helpers';

/**
 * Calculate G12w (Weekend tariff) tariff cost
 *
 * Formula:
 * - Weekday: day (06:00-22:00) and night (22:00-06:00) rates
 * - Weekend: all-day cheap rate (same as weekday night rate)
 * - Energy cost = kWh × energyPrice + qualityCharge
 * - Distribution cost = fixedFee + (kWh × distributionRate by zone)
 * - Total (gross) = (Energy + Distribution) × 1.23
 *
 * @param monthlyConsumption - kWh consumed in month
 * @param pricing - G12w pricing data
 * @param consumptionPattern - Hourly pattern (weekday/weekend)
 * @param daysInMonth - Number of days in month
 * @param year - Year (for weekend detection)
 * @param month - Month (0-indexed, 0=Jan)
 * @returns Monthly calculation result with weekday/weekend split
 */
export function calculateG12w(
  monthlyConsumption: number,
  pricing: G12wPricing,
  consumptionPattern: ConsumptionPattern,
  daysInMonth: number,
  year: number,
  month: number,
  osd?: string
): MonthlyResult {
  // Input validation - HIGH-1 Security: Validate for NaN, Infinity, and negative values
  if (!Number.isFinite(monthlyConsumption) || monthlyConsumption < 0) {
    throw new Error('Monthly consumption must be a finite non-negative number');
  }
  if (!pricing) {
    throw new Error('Pricing data is required');
  }
  if (!consumptionPattern) {
    throw new Error('Consumption pattern is required');
  }

  // Distribute consumption across hours
  const hourlyConsumption = distributeConsumption(
    monthlyConsumption,
    consumptionPattern,
    daysInMonth,
    year,
    month
  );

  // Split into weekday day, weekday night, and weekend consumption
  let weekdayDayKwh = 0;
  let weekdayNightKwh = 0;
  let weekendKwh = 0;

  for (const hourData of hourlyConsumption) {
    if (hourData.isWeekend || hourData.isHoliday) {
      // Weekend: all hours use cheap rate
      weekendKwh += hourData.consumption;
    } else {
      // Weekday: split by time (06:00-13:00 + 15:00-22:00 = day, rest = night)
      if (osd ? isG12DayHourForOSD(hourData.hour, osd, month) : isG12DayHour(hourData.hour)) {
        weekdayDayKwh += hourData.consumption;
      } else {
        weekdayNightKwh += hourData.consumption;
      }
    }
  }

  // Calculate energy production cost (same rate for all zones)
  const energyPriceTotal = pricing.energyPricePerKwh + (pricing.qualityCharge || 0);
  const productionCost = monthlyConsumption * energyPriceTotal;

  // Calculate distribution cost
  const distributionWeekdayDayCost = weekdayDayKwh * pricing.distributionDayPerKwh;
  const distributionWeekdayNightCost = weekdayNightKwh * pricing.distributionNightPerKwh;
  const distributionWeekendCost = weekendKwh * pricing.distributionWeekendPerKwh;
  const distributionCost = pricing.fixedMonthlyFee + distributionWeekdayDayCost + distributionWeekdayNightCost + distributionWeekendCost;

  // Get fees - use defaults if not provided
  const handlingFee = pricing.handlingFee ?? HANDLING_FEE.REGULATED;
  const subscriptionFee = pricing.subscriptionFee ?? SUBSCRIPTION_FEE;
  const transitionFee = pricing.transitionFee ?? TRANSITION_FEE.TIER_3;

  // Power fee is 0 for Jan-June 2025
  const basePowerFee = pricing.powerFee ?? POWER_FEE.TIER_3;
  const powerFee = isPowerFeeFrozen(month, year) ? 0 : basePowerFee;

  // Variable fees (per kWh)
  const ozeFeeAmount = monthlyConsumption * (pricing.ozeFee ?? OZE_FEE);
  const cogenerationFeeAmount = monthlyConsumption * (pricing.cogenerationFee ?? COGENERATION_FEE);

  // Calculate totals
  const subtotal = productionCost + distributionCost +
    handlingFee + subscriptionFee + transitionFee + powerFee +
    ozeFeeAmount + cogenerationFeeAmount;
  const totalCost = subtotal * (1 + VAT_RATE);

  return {
    totalKwh: roundToTwoDecimals(monthlyConsumption),
    dayKwh: roundToTwoDecimals(weekdayDayKwh),
    nightKwh: roundToTwoDecimals(weekdayNightKwh),
    weekendKwh: roundToTwoDecimals(weekendKwh),
    productionCost: roundToTwoDecimals(productionCost),
    distributionCost: roundToTwoDecimals(distributionCost),
    handlingFee: roundToTwoDecimals(handlingFee),
    subscriptionFee: roundToTwoDecimals(subscriptionFee),
    transitionFee: roundToTwoDecimals(transitionFee),
    powerFee: roundToTwoDecimals(powerFee),
    ozeFee: roundToTwoDecimals(ozeFeeAmount),
    cogenerationFee: roundToTwoDecimals(cogenerationFeeAmount),
    subtotal: roundToTwoDecimals(subtotal),
    totalCost: roundToTwoDecimals(totalCost)
  };
}
